import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useUserProfile } from '../../contexts/UserProfileContext';

// ─── Animations ───────────────────────────────────────────────────────────────
const overlayFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalEnter = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const checkScale = keyframes`
  0% { transform: scale(0); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

const guideEnter = keyframes`
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const guidePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(76, 110, 245, 0.25); }
  50% { box-shadow: 0 0 0 8px rgba(76, 110, 245, 0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: ${overlayFade} 0.25s ease;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 18px;
  width: 100%;
  max-width: 720px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.04);
  animation: ${modalEnter} 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  display: flex;
  flex-direction: row;

  @media (max-width: 640px) {
    flex-direction: column;
    max-height: 92vh;
    overflow-y: auto;
  }
`;

const LeftPanel = styled.div`
  width: 220px;
  flex-shrink: 0;
  background: linear-gradient(160deg, #4c6ef5 0%, #3b5bdb 60%, #364fc7 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  gap: 16px;
  position: relative;

  @media (max-width: 640px) {
    width: 100%;
    padding: 24px 20px;
    flex-direction: row;
    gap: 16px;
    justify-content: flex-start;
  }
`;

const PhotoRing = styled.div<{ $hasImage: boolean }>`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 3px solid ${p => p.$hasImage ? 'rgba(34, 197, 94, 0.8)' : 'rgba(255, 255, 255, 0.35)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  background: ${p => p.$hasImage ? 'transparent' : 'rgba(255, 255, 255, 0.08)'};
  flex-shrink: 0;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.12);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 72px;
    height: 72px;
  }
`;

const PhotoPlaceholderInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.5);

  svg { opacity: 0.6; }

  span {
    font-size: 0.65rem;
    font-weight: 500;
    text-align: center;
    line-height: 1.3;
  }
`;

const CheckBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #22c55e;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2.5px solid #4c6ef5;
  animation: ${checkScale} 0.35s ease;

  svg { width: 12px; height: 12px; stroke: #fff; stroke-width: 3; }
`;

const LeftInfo = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.92);

  @media (max-width: 640px) {
    text-align: left;
  }
`;

const UploadLink = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

const ProgressBar = styled.div`
  width: 80%;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.8) 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1s linear infinite;
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 28px 28px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;

  @media (max-width: 640px) {
    padding: 20px 20px 24px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.18rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const ModalSub = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.5;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = styled.label`
  font-size: 0.76rem;
  font-weight: 700;
  color: #334155;
`;

const FieldInput = styled.input`
  padding: 9px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #1e293b;
  outline: none;
  transition: all 0.2s;
  background: #fff;

  &:focus { border-color: #4c6ef5; box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.08); }
`;

const ReadOnlyField = styled.div`
  padding: 9px 12px;
  border: 1.5px solid #f1f5f9;
  border-radius: 8px;
  font-size: 0.82rem;
  color: #94a3b8;
  background: #f8fafc;
`;

const FieldPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const HintBar = styled.div`
  font-size: 0.7rem;
  color: #b45309;
  background: linear-gradient(135deg, #fffbeb 0%, #fef9c3 100%);
  border: 1px solid #fde68a;
  border-radius: 7px;
  padding: 7px 10px;
  line-height: 1.4;
  display: flex;
  gap: 6px;
  align-items: flex-start;
`;

const ErrText = styled.div`
  font-size: 0.72rem;
  color: #ef4444;
  font-weight: 500;
`;

const GuideOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  animation: ${overlayFade} 0.2s ease;
  padding: 16px;
`;

const GuideCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 28px 24px 24px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2);
  animation: ${guideEnter} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  text-align: center;
`;

const GuideClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
  }
`;

const GuideIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  animation: ${guidePulse} 2s ease infinite;
`;

const GuideTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1e293b;
`;

const GuideText = styled.p`
  margin: 0 0 16px;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.55;
`;

const GuideExamples = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
`;

const GuideExample = styled.div<{ $good: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${p => p.$good ? '#f0fdf4' : '#fef2f2'};
  color: ${p => p.$good ? '#15803d' : '#dc2626'};
  border: 1px solid ${p => p.$good ? '#bbf7d0' : '#fecaca'};
`;

const GuideOkBtn = styled.button`
  padding: 10px 32px;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: #fff;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(76, 110, 245, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(76, 110, 245, 0.4);
  }
`;

const SaveBtn = styled.button<{ $ready: boolean }>`
  padding: 11px 20px;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: ${p => p.$ready ? 'pointer' : 'not-allowed'};
  background: ${p => p.$ready
    ? 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)'
    : 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)'};
  color: #fff;
  transition: all 0.3s;
  box-shadow: ${p => p.$ready ? '0 4px 14px rgba(76, 110, 245, 0.3)' : 'none'};
  margin-top: 2px;
  width: 100%;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(76, 110, 245, 0.38);
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
interface FirstTimeProfileModalProps {
  onComplete: () => void;
}

const FirstTimeProfileModal: React.FC<FirstTimeProfileModalProps> = ({ onComplete }) => {
  const {
    email,
    phoneNumber,
    fullName: existingFullName,
    profileImageUrl: existingImageUrl,
    updateProfileDetails,
    startImageUploadAnimation,
    processImageUpload,
    completeImageAnimation,
    isSavingProfile,
    isUploadingImage,
  } = useUserProfile();

  const hasExistingImage = !!existingImageUrl;
  const existingNameUsable = existingFullName && existingFullName.trim().toLowerCase() !== 'new user' && existingFullName.trim().split(/\s+/).length >= 2;

  const [name, setName] = useState(existingNameUsable ? existingFullName.trim() : '');
  const [nameError, setNameError] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(hasExistingImage ? existingImageUrl : null);
  const [imageError, setImageError] = useState('');
  const [imageUploaded, setImageUploaded] = useState(hasExistingImage);
  const [saving, setSaving] = useState(false);
  const [showNameGuide, setShowNameGuide] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const validateName = useCallback((val: string): string => {
    const trimmed = val.trim();
    if (!trimmed) return 'Full name is required';
    if (trimmed.length < 3) return 'Name must be at least 3 characters';
    if (!/^[a-zA-Z\s.'-]+$/.test(trimmed)) return 'Only letters, spaces, dots, hyphens and apostrophes allowed';
    if (trimmed.split(/\s+/).length < 2) return 'Please enter your full name (first & last name)';
    return '';
  }, []);

  const validateImage = useCallback((file: File): Promise<string> => {
    return new Promise((resolve) => {
      const allowed = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowed.includes(file.type)) { resolve('Only JPG/PNG images are allowed'); return; }
      if (file.size > 5 * 1024 * 1024) { resolve('Image must be under 5 MB'); return; }
      if (file.size < 10 * 1024) { resolve('Image is too small — upload a clear photo'); return; }
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.width < 150 || img.height < 150) { resolve('Resolution too low (min 150x150)'); return; }
        const ratio = img.width / img.height;
        if (ratio > 3 || ratio < 0.3) { resolve('Please upload a proper photo'); return; }
        resolve('');
      };
      img.onerror = () => resolve('Could not read the image');
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const err = await validateImage(file);
    if (err) { setImageError(err); if (fileRef.current) fileRef.current.value = ''; return; }

    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
    setImageUploaded(false);
    startImageUploadAnimation(preview);
    const ok = await processImageUpload(file, preview);
    if (ok) { setImageUploaded(true); completeImageAnimation(preview); }
    else { setImageError('Upload failed. Please try again.'); setImagePreview(null); }
    if (fileRef.current) fileRef.current.value = '';
  }, [validateImage, startImageUploadAnimation, processImageUpload, completeImageAnimation]);

  const handleSave = useCallback(async () => {
    const nameErr = validateName(name);
    if (nameErr) { setNameError(nameErr); return; }
    if (!imageUploaded) { setImageError('Please upload your profile photo'); return; }
    setSaving(true);
    try {
      await updateProfileDetails({ fullName: name.trim() });
      onComplete();
    } catch { setNameError('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  }, [name, imageUploaded, validateName, updateProfileDetails, onComplete]);

  // Block Escape key — modal is non-skippable (but allow closing guide)
  useEffect(() => {
    const block = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showNameGuide) { setShowNameGuide(false); return; }
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', block);
    return () => document.removeEventListener('keydown', block);
  }, [showNameGuide]);

  const canSave = name.trim().split(/\s+/).length >= 2 && name.trim().length >= 3 && imageUploaded && !saving && !isUploadingImage;

  return (
    <Overlay>
      <ModalCard>
        {/* ── Left: Photo Upload ── */}
        <LeftPanel>
          <PhotoRing $hasImage={!!imagePreview} onClick={() => fileRef.current?.click()}>
            {imagePreview ? (
              <>
                <img src={imagePreview} alt="Profile" />
                {imageUploaded && (
                  <CheckBadge>
                    <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </CheckBadge>
                )}
              </>
            ) : (
              <PhotoPlaceholderInner>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>Your photo</span>
              </PhotoPlaceholderInner>
            )}
          </PhotoRing>

          {isUploadingImage && <ProgressBar />}

          <LeftInfo>
            <UploadLink onClick={() => fileRef.current?.click()} type="button">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {imagePreview ? 'Change Photo' : 'Upload Photo'}
            </UploadLink>
          </LeftInfo>

          {imageError && (
            <div style={{ fontSize: '0.7rem', color: '#fca5a5', textAlign: 'center', lineHeight: 1.35 }}>
              {imageError}
            </div>
          )}

          <input
            ref={fileRef} type="file" accept="image/jpeg,image/png,image/jpg"
            style={{ display: 'none' }} onChange={handleFileSelect}
          />
        </LeftPanel>

        {/* ── Right: Form ── */}
        <RightPanel>
          <div>
            <ModalTitle>Complete Your Profile</ModalTitle>
            <ModalSub>
              Fill in your details to get started with your CNG Dealership application.
              <span style={{ color: '#ef4444', fontWeight: 700 }}> All fields are mandatory.</span>
            </ModalSub>
          </div>

          <FieldRow>
            <FieldLabel>Full Name <span style={{ color: '#ef4444' }}>*</span></FieldLabel>
            <FieldInput
              type="text"
              placeholder="e.g. Rajesh Kumar Sharma"
              value={name}
              onChange={e => { setName(e.target.value); setNameError(''); }}
              maxLength={100}
              autoFocus
            />
            {nameError && <ErrText>{nameError}</ErrText>}
            <HintBar>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>Enter your name <strong>exactly as shown in your Aadhaar Card / PAN Card</strong>. Mismatch may lead to application rejection.</span>
            </HintBar>
          </FieldRow>

          <FieldPair>
            <FieldRow>
              <FieldLabel>Email</FieldLabel>
              <ReadOnlyField>{email || '\u2014'}</ReadOnlyField>
            </FieldRow>
            <FieldRow>
              <FieldLabel>Mobile</FieldLabel>
              <ReadOnlyField>{phoneNumber || '\u2014'}</ReadOnlyField>
            </FieldRow>
          </FieldPair>

          <div
            onClick={() => {
              if (!canSave) {
                const trimmed = name.trim();
                if (!trimmed) { setNameError('Full name is required'); return; }
                if (trimmed.split(/\s+/).length < 2) { setShowNameGuide(true); return; }
                if (!imageUploaded) { setImageError('Please upload your profile photo'); return; }
              }
            }}
          >
            <SaveBtn
              $ready={canSave}
              disabled={!canSave}
              onClick={handleSave}
            >
              {saving || isSavingProfile ? 'Saving\u2026' : 'Save & Continue'}
            </SaveBtn>
          </div>
        </RightPanel>
      </ModalCard>
      {showNameGuide && (
        <GuideOverlay onClick={() => setShowNameGuide(false)}>
          <GuideCard onClick={e => e.stopPropagation()}>
            <GuideClose onClick={() => setShowNameGuide(false)}>&times;</GuideClose>
            <GuideIcon>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4c6ef5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </GuideIcon>
            <GuideTitle>Enter Your Full Name</GuideTitle>
            <GuideText>
              Please enter <strong>both your first name and last name</strong>, separated by a space — exactly as shown on your <strong>Aadhaar Card</strong> or <strong>PAN Card</strong>.
            </GuideText>
            <GuideExamples>
              <GuideExample $good={true}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                Rajesh Sharma
              </GuideExample>
              <GuideExample $good={true}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                Priya Kumari Singh
              </GuideExample>
              <GuideExample $good={false}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                Rajesh
              </GuideExample>
              <GuideExample $good={false}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                WOWOOOA
              </GuideExample>
            </GuideExamples>
            <GuideOkBtn onClick={() => setShowNameGuide(false)}>Got it</GuideOkBtn>
          </GuideCard>
        </GuideOverlay>
      )}
    </Overlay>
  );
};

export default FirstTimeProfileModal;
